import {
  SyncChangeSchema,
  TripStatusSchema,
  TripWithItemsSchema,
  type SyncChange,
  type TripStatus,
  type TripItem,
  type TripItemPatch,
  type TripWithItems,
} from '@sp/shared'
import { keysWithPrefix, readJson, removeKey, writeJson } from './storage.ts'

/**
 * Offline vrstva nakupu.
 *
 * Kazda zmena polozek (pridani, odebrani, uprava) se nejdriv zapise sem a do
 * obrazovky, teprve pak se zkousi odeslat. Kdyz v obchode neni signal, zmeny
 * cekaji ve fronte a odejdou samy. Stav nakupu se taky drzi lokalne — diky
 * tomu prezije i tichou obnovu stranky po nasazeni nove verze.
 *
 * localStorage misto IndexedDB: jeden nakup jsou desitky polozek, tedy
 * jednotky kilobajtu. IndexedDB by tu byla slozitost bez prinosu.
 */
export type QueuedChange = SyncChange

const QUEUE_PREFIX = 'sp:fronta:'
const queueKey = (tripId: string) => `${QUEUE_PREFIX}${tripId}`
/** Stav nakupu (Nakupuju, Hotovo) ceka na signal stejne jako zmeny polozek. */
const STATUS_PREFIX = 'sp:nakup-stav:'
const statusKey = (tripId: string) => `${STATUS_PREFIX}${tripId}`

export function loadQueuedStatus(tripId: string): TripStatus | null {
  const parsed = TripStatusSchema.safeParse(readJson(statusKey(tripId)))
  return parsed.success ? parsed.data : null
}

export function saveQueuedStatus(tripId: string, status: TripStatus | null): void {
  if (status) writeJson(statusKey(tripId), status)
  else removeKey(statusKey(tripId))
}

/** Nakupy, ktere maji neco k odeslani (polozky nebo stav). */
export function tripsWithOutbox(): string[] {
  return [
    ...new Set([
      ...keysWithPrefix(QUEUE_PREFIX).map((key) => key.slice(QUEUE_PREFIX.length)),
      ...keysWithPrefix(STATUS_PREFIX).map((key) => key.slice(STATUS_PREFIX.length)),
    ]),
  ]
}
const tripKey = (tripId: string) => `sp:nakup:${tripId}`

/**
 * Validuje se po polozkach, ne celym polem. Po nasazeni nove verze muze
 * fronta obsahovat zmenu ve starem tvaru; zahodit jednu je lepsi nez celou
 * frontu neodeslanych odskrtnuti.
 */
export function loadQueue(tripId: string): QueuedChange[] {
  const raw = readJson(queueKey(tripId))
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry) => {
    const parsed = SyncChangeSchema.safeParse(entry)
    return parsed.success ? [parsed.data] : []
  })
}

export function saveQueue(tripId: string, queue: readonly QueuedChange[]): void {
  if (queue.length > 0) writeJson(queueKey(tripId), queue)
  else removeKey(queueKey(tripId))
}

/** Pocet neodeslanych zmen napric nakupy — pred odhlasenim se na to ptame. */
export function pendingChangesCount(): number {
  return tripsWithOutbox().reduce(
    (sum, tripId) => sum + loadQueue(tripId).length + (loadQueuedStatus(tripId) ? 1 : 0),
    0,
  )
}

export function loadCachedTrip(tripId: string): TripWithItems | null {
  const parsed = TripWithItemsSchema.safeParse(readJson(tripKey(tripId)))
  return parsed.success ? parsed.data : null
}

export function saveCachedTrip(trip: TripWithItems): void {
  writeJson(tripKey(trip.id), trip)
}

export function forgetTrip(tripId: string): void {
  removeKey(tripKey(tripId))
  removeKey(queueKey(tripId))
  removeKey(statusKey(tripId))
  // Slevy nakupu (useTripDeals) — velke odpovedi, po smazani nakupu k nicemu.
  removeKey(`sp:slevy:${tripId}`)
  removeKey(`sp:slevy-skryto:${tripId}`)
}

export function applyPatch(item: TripItem, patch: TripItemPatch, userId: string, at: string): TripItem {
  const next: Record<string, unknown> = { ...item }
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) next[key] = value
  }
  if (patch.status !== undefined) next['checkedBy'] = patch.status === 'pending' ? null : userId
  next['updatedAt'] = at
  next['updatedBy'] = userId
  return next as TripItem
}

/** Jedna zmena z fronty promitnuta do polozek — stejne, jak ji provede server. */
export function applyChange(
  items: readonly TripItem[],
  change: QueuedChange,
  tripId: string,
  userId: string,
): TripItem[] {
  switch (change.op) {
    case 'add': {
      if (items.some((i) => i.id === change.item.id)) return [...items]
      const position = items.reduce((max, i) => Math.max(max, i.position), -1) + 1
      return [
        ...items,
        {
          ...change.item,
          tripId,
          status: 'pending',
          actualPriceMinor: null,
          position,
          createdBy: userId,
          checkedBy: null,
          updatedAt: change.changedAt,
          updatedBy: userId,
        },
      ]
    }
    case 'remove':
      return items.filter((i) => i.id !== change.itemId)
    case 'patch':
      return items.map((i) => (i.id === change.itemId ? applyPatch(i, change.patch, userId, change.changedAt) : i))
  }
}

/** Znovu aplikuje neodeslane zmeny na cerstva data ze serveru. */
export function applyQueue(trip: TripWithItems, queue: readonly QueuedChange[], userId: string): TripWithItems {
  if (queue.length === 0) return trip
  return {
    ...trip,
    items: queue.reduce<TripItem[]>((items, change) => applyChange(items, change, trip.id, userId), trip.items),
  }
}
